import { ColumnDef } from "@lhd/components/Table/hook";
import { ICellRendererParams } from "ag-grid-community";
import { MAP_COMPONENT } from "./fields/constant";
import useValidation from "./useValidation";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import { useEffect } from "react";

const CellEditRenderer = (params: Partial<ICellRendererParams>) => {
  const colDef = (params.colDef || {}) as ColumnDef;
  const { editInfo, field } = colDef;
  const dataValidationRef = params.context?.dataValidationRef;

  const getRowIdForForm =
    colDef.getRowIdForForm ||
    ((data: any) => {
      return data.id;
    });
  const rowIdRaw = getRowIdForForm(params.data);
  const colIdRaw = field;
  const isMissingCellId =
    rowIdRaw === undefined ||
    rowIdRaw === null ||
    rowIdRaw === "" ||
    colIdRaw === undefined ||
    colIdRaw === null ||
    colIdRaw === "";
  const rowId = String(rowIdRaw);
  const colId = String(colIdRaw);

  const changedValue =
    params.context?.dataChangedRef?.current?.[String(rowId)]?.[String(colId)];
  const originalValue = params.valueFormatted ?? params.value;
  const defaultValue = changedValue ?? originalValue;
  const { handleValidation, isError } = useValidation({
    colDef: colDef,
    dataChangedRef: params.context?.dataChangedRef,
    handleForceRender: params.context?.handleForceRender,
    dataValidationRef: dataValidationRef,
    rowId,
    colId,
    originalValue,
  });

  const keyAsCom = editInfo?.type as keyof typeof MAP_COMPONENT;
  const Com = editInfo?.editCellRenderer || MAP_COMPONENT[keyAsCom];
  const getDisabledTimeData = editInfo?.getDisabledTimeData
    ? () =>
        editInfo.getDisabledTimeData?.({
          ...params.data,
          ...(params.context?.dataChangedRef?.current?.[rowId] ?? {}),
        })
    : undefined;
  const getCellValue = (relatedField: string) => {
    const relatedColumnId =
      colId.includes(".") && !relatedField.includes(".")
        ? `${colId.slice(0, colId.lastIndexOf(".") + 1)}${relatedField}`
        : relatedField;
    const rowChanges = params.context?.dataChangedRef?.current?.[rowId] ?? {};

    return Object.prototype.hasOwnProperty.call(rowChanges, relatedColumnId)
      ? rowChanges[relatedColumnId]
      : get(params.data, relatedColumnId);
  };

  const handleSetValue = ({
    rowIdParam = rowId,
    colIdParam = colId,
    value,
  }: {
    rowIdParam?: string;
    colIdParam?: string;
    value: any;
  }) => {
    const dataChangedRef = params.context?.dataChangedRef;
    const hasValueBeforeSetValue = !isEmpty(dataChangedRef?.current);

    if (!dataChangedRef || !colIdParam || !rowIdParam) return;

    if (!dataChangedRef.current[rowIdParam]) {
      dataChangedRef.current[rowIdParam] = {};
    }
    dataChangedRef.current[rowIdParam][colIdParam] = value;
    if (!hasValueBeforeSetValue) {
      params.context?.handleForceRender();
    }
  };

  const handleChange = (eventOrValue: any) => {
    eventOrValue?.stopPropagation?.();

    const value = eventOrValue?.target
      ? eventOrValue.target.type === "checkbox"
        ? eventOrValue.target.checked
        : eventOrValue.target.value
      : eventOrValue;
    const dataChangedRef = params.context?.dataChangedRef;

    if (!dataChangedRef || isMissingCellId) return;
    handleSetValue({ value });
    handleValidation();
  };

  const handleRefreshRow = () => {
    params.node &&
      params?.api?.refreshCells({
        rowNodes: [params.node],
        force: true,
      });
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
      data-row-id={rowId}
      data-col-id={colId}
      data-type={colDef.editInfo?.type}
      className={`${isError ? "has-error" : ""}`}
    >
      {Com ? (
        <Com
          defaultValue={defaultValue}
          onChange={handleChange}
          handleRefreshRow={handleRefreshRow}
          colDef={colDef}
          rowId={rowId}
          colId={colId}
          rowData={params.data}
          getCellValue={getCellValue}
          getDisabledTimeData={getDisabledTimeData}
          handleSetValue={handleSetValue}
        />
      ) : (
        keyAsCom
      )}
    </div>
  );
};
export default CellEditRenderer;
